import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

/**
 * ViewModeContext
 * mode:        'auto' | 'desktop' | 'tablet' | 'mobile'  (user choice, persisted)
 * effective:   'desktop' | 'tablet' | 'mobile'           (resolved against window width when auto)
 *
 * The effective mode is also written to <html data-viewmode="..."> so that
 * global CSS rules (index.css) can adapt layout, font sizes, spacing, etc.
 */

const ViewModeContext = createContext(null);

const STORAGE_KEY = "rh-view-mode";
const VALID = ["auto", "desktop", "tablet", "mobile"];

function detectAuto() {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && VALID.includes(v)) return v;
  } catch (_) { /* ignore */ }
  return "auto";
}

export function ViewModeProvider({ children }) {
  const [mode, setModeState] = useState(() => readStored());
  const [autoEffective, setAutoEffective] = useState(() => detectAuto());

  // Recompute auto effective on resize (only when in auto)
  useEffect(() => {
    if (mode !== "auto") return;
    const handler = () => setAutoEffective(detectAuto());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [mode]);

  const effective = mode === "auto" ? autoEffective : mode;

  // Mirror to <html data-viewmode> and persist
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-viewmode", effective);
      document.documentElement.setAttribute("data-viewmode-choice", mode);
    }
    try { localStorage.setItem(STORAGE_KEY, mode); } catch (_) { /* ignore */ }
  }, [effective, mode]);

  const setMode = useCallback((next) => {
    if (!VALID.includes(next)) return;
    setModeState(next);
    if (next === "auto") setAutoEffective(detectAuto());
  }, []);

  const value = useMemo(() => ({
    mode,
    effective,
    setMode,
    isMobile: effective === "mobile",
    isTablet: effective === "tablet",
    isDesktop: effective === "desktop",
  }), [mode, effective, setMode]);

  return (
    <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>
  );
}

export function useViewMode() {
  const ctx = useContext(ViewModeContext);
  if (!ctx) throw new Error("useViewMode must be used within ViewModeProvider");
  return ctx;
}
